// src/features/task/TaskAttachments.tsx
// Attachments section for the task detail page. Files uploaded at creation
// (or linked later) land as RELATION_TYPE_ATTACHMENT resource links on the
// task entity — the same data already fetched for the Right Rail's Resources
// tab via entityStore.loadResources(), so this reuses that observable state
// instead of firing a second request. Download links are resolved on demand
// via attachmentApi.GetDownloadUrl.

import { useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import { attachmentApi } from '@core/api';
import { resourceStore } from '@core/stores/resource/ResourceStore';
import { entityStore } from '@/stores/EntityStore';
import { useTabId } from '@/components/tabs/TabContext';
import { entityToUrn, urnToEntity } from '@/features/resources/logic';
import { formatDateTime } from '@/features/task/logic';
import { TaskSection } from '@/features/task/detail/TaskSection';
import { useTaskSection } from '@/features/task/detail/sections';
import { errMsg } from '@/features/task/panelHelpers';
import { downloadAttachment } from '@/features/attachment/download';
import { UserName } from '@/features/userprofile/UserName';

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

export const TaskAttachments = observer(({ tabId: tabIdProp }: { tabId?: string }) => {
  const { t } = useI18n();
  const ownTabId = useTabId();
  // The rail mounts this outside any pane: the page's tab id comes in as a
  // prop, since useTabId() there resolves to the detached tab.
  const tabId = tabIdProp ?? ownTabId;
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The task page fills the context of its own tab, which is not necessarily
  // the tab in front — a background pane must still list its own files.
  const context = entityStore.stateFor(tabId);
  const attachments = context.resources.filter((r) => r.relation_type === 'RELATION_TYPE_ATTACHMENT');
  const loading = context.isLoadingResources;
  const taskUrn = context.currentEntity?.urn || '';
  const attachmentIds = attachments.map((item) => item.entity?.id || '').filter(Boolean).join('|');

  useEffect(() => {
    if (!attachmentIds) { setFileNames({}); return; }
    let active = true;
    attachmentApi.ListAttachmentsByIDs({ ids: attachmentIds.split('|') }).then((response) => {
      if (!active) return;
      setFileNames(Object.fromEntries((response.attachments?.attachments || [])
        .filter((item) => item.entity?.id && item.file_name)
        .map((item) => [item.entity!.id, item.file_name])));
    }).catch(() => { if (active) setFileNames({}); });
    return () => { active = false; };
  }, [attachmentIds]);

  const download = async (attachmentId: string) => {
    setDownloadingId(attachmentId);
    setError(null);
    try {
      await downloadAttachment(attachmentId, fileNames[attachmentId] || attachments.find((item) => item.entity?.id === attachmentId)?.title || '');
    } catch (e) {
      setError(errMsg(e, t('task.attachmentsPanel.downloadError')));
    } finally {
      setDownloadingId(null);
    }
  };

  // Upload mirrors useTaskForm's create-time flow (presigned URL → PUT →
  // ConfirmUpload), then links the attachment to the task as a resource so it
  // shows up here and on the Right Rail.
  const upload = async (files: FileList | null) => {
    const file = files?.[0];
    const source = urnToEntity(taskUrn);
    if (!file || !source) return;
    setUploading(true);
    setError(null);
    try {
      const urlResponse = (await attachmentApi.GetUploadUrl({
        file_name: file.name,
        mime_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
      } as never)) as { upload_url?: { url?: string }; upload_id?: string };
      const uploadUrl = urlResponse?.upload_url?.url;
      const uploadId = urlResponse?.upload_id;
      if (!uploadUrl || !uploadId) throw new Error('no upload url');

      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!putResponse.ok) throw new Error('upload failed');

      const confirmed = (await attachmentApi.ConfirmUpload({ upload_id: uploadId } as never)) as {
        entity?: { id?: string };
        attachment?: { entity?: { id?: string } };
      };
      const attachmentEntity = (confirmed as any)?.entity || (confirmed as any)?.attachment?.entity;
      const attachmentUrn = entityToUrn(attachmentEntity);
      if (!attachmentUrn) throw new Error('no attachment entity');

      await resourceStore.LinkResources({
        source: source as any,
        target: urnToEntity(attachmentUrn) as any,
        relation_type: 'RELATION_TYPE_ATTACHMENT' as any,
        relation_label: file.name,
      });
      if (context.currentEntity) {
        await entityStore.loadResources(tabId, context.currentEntity);
      }
    } catch (e) {
      setError(errMsg(e, t('task.attachmentsPanel.uploadError')));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Nothing here means nothing drawn; the page offers this part instead.
  const visible = useTaskSection('attachments', attachments.length > 0);
  if (!visible) return null;

  return (
    <TaskSection
      title={t('task.attachments')}
      action={
        <>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            aria-label={t('task.attachmentsPanel.upload')}
            onChange={(e) => upload(e.target.files)}
          />
          <Button
            size="sm"
            variant="ghost"
            isLoading={uploading}
            disabled={!taskUrn}
            onClick={() => fileInputRef.current?.click()}
          >
            {t('task.attachmentsPanel.upload')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        {error && <Alert variant="error">{error}</Alert>}

        {loading ? (
          <div className="h-10 animate-pulse rounded-md bg-muted" aria-busy="true" />
        ) : attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.attachmentsPanel.empty')}</p>
        ) : (
          <div className="divide-y divide-border">
            {attachments.map((a) => {
              const id = a.entity?.id || '';
              return (
                <div key={id} className="py-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-foreground truncate">{fileNames[id] || a.title || id.substring(0, 8)}</p>
                      {a.linked_at && (
                        <p className="text-xs text-muted-foreground">
                          {a.linked_by?.entity?.id && <>{t('task.attachmentsPanel.addedBy')} <UserName id={a.linked_by.entity.id} /> · </>}
                          {formatDateTime(a.linked_at)}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    isLoading={downloadingId === id}
                    disabled={!id}
                    onClick={() => download(id)}
                  >
                    {t('task.attachmentsPanel.download')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TaskSection>
  );
});

export default TaskAttachments;
